import * as THREE from "three";
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import FontFaceObserver from 'fontfaceobserver';
import { getFontFamily } from "utils/functions.utils";
import { IObject, ObjectTypes } from "utils/interface.utils";
import { updateObjectInTargetImage } from "store/reducers/editorSlice";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";

export const createObjectText = async (workspace: WorkspaceContext, objectID: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, targetImageRef, objectsRef, selectedTransformControlRef } = workspace.refs;
    const { selectedObjectID } = workspace;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const targetImageGroup = targetImageRef.current;

    try {
        const objectText = await renderObjectText(workspace, scene, properties, objectID, targetImageGroup);
        if (objectText && objectText.mesh) {
            objectsRef.current.set(
                objectID,
                {
                    mesh: objectText.mesh,
                    cleanup: objectText.cleanup
                }
            )

            // Text creation is async (font load via FontFaceObserver), so the box-creation
            // effect gated on selectedTransformControl can run and find no mesh yet if the
            // user activates a transform tool right after adding the object — it never
            // retries once the mesh shows up. Reattach here too if that race left this
            // object selected with a tool already active by the time creation finishes.
            // A build that lost the race to a newer one (add + sync both create) resolves with a mesh
            // that is already out of the scene; a box on it would be an orphan nothing removes.
            if (objectID === selectedObjectID && selectedTransformControlRef.current && objectText.mesh.parent) {
                renderTransformBox(workspace, objectText.mesh);
            }

            renderer.render(scene, camera);
        }
    } catch (error) {
        console.log("Error creating object text:", error);
    }
}

export const updateObjectText = async (workspace: WorkspaceContext, objectID: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, dragSystemRef, rotationSystemRef, scaleSystemRef, targetImageRef, objectsRef, textUpdateGenerationRef, selectedTransformControlRef } = workspace.refs;
    const { selectedObjectID } = workspace;
    if (dragSystemRef.current?.isObjectBeingDraggedAndPositioned(objectID)) {
        console.log(`Skipping text update for ${objectID} - currently being dragged`);
        return;
    }

    if (rotationSystemRef.current?.isObjectBeingDraggedAndRotated(objectID)) {
        console.log(`Skipping text update for ${objectID} - currently being rotated`);
        return;
    }

    if (scaleSystemRef.current?.isObjectBeingDraggedAndScaled(objectID)) {
        console.log(`Skipping text update for ${objectID} - currently being scaled`);
        return;
    }

    const objectData = objectsRef.current.get(objectID);
    if (!objectData || !objectData.mesh) return;

    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const targetImageGroup = targetImageRef.current;

    if (objectData.cleanup) {
        objectData.cleanup();
    }

    // Claim this generation before the async rebuild, so a call that starts later for the
    // same object (and therefore resolves as the "real" latest state) can tell this one it's
    // been superseded once it comes back.
    const myGeneration = (textUpdateGenerationRef.current.get(objectID) ?? 0) + 1;
    textUpdateGenerationRef.current.set(objectID, myGeneration);

    try {
        const updatedText = await renderObjectText(workspace, scene, properties, objectID, targetImageGroup, myGeneration);

        // If a newer updateObjectText call started for this object while this one was still
        // rebuilding its mesh (font load is async), renderObjectText detects it's no longer
        // the latest generation and resolves null before touching the scene at all — so there
        // is nothing here to store or clean up.
        if (updatedText && updatedText.mesh) {
            objectsRef.current.set(
                objectID,
                {
                    mesh: updatedText.mesh,
                    cleanup: updatedText.cleanup
                }
            )

            // Unlike image/video/social, text objects are torn down and rebuilt into a brand
            // new mesh on every properties update (font metrics require a fresh geometry),
            // which drops any transform box attached to the old mesh. If this object is
            // currently selected with a transform tool active, reattach the box to the new
            // mesh so it doesn't silently disappear after the next unrelated store update.
            if (objectID === selectedObjectID && selectedTransformControlRef.current) {
                renderTransformBox(workspace, updatedText.mesh);
            }

            renderer.render(scene, camera);
        }
    } catch (error) {
        console.log("Error updating object text:", error);
    }
}

export const renderObjectText = (workspace: WorkspaceContext, scene: THREE.Scene, properties: IObject, objectID: string, targetImageGroup: THREE.Group | null, generation?: number): Promise<{ mesh: THREE.Mesh, cleanup: () => void } | null> => {
    const { textUpdateGenerationRef } = workspace.refs;
    const { dispatch, selectedTargetImageID } = workspace;
    return new Promise((resolve) => {

        const { text, fontFamily, fontSize, fontStyle, fontColor, bgColor, positionX, positionY, scaleX, scaleY, rotationX, renderOrder } = properties;

        const parentObject = targetImageGroup || scene;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            console.error("Failed to get 2D context");
            resolve(null);
            return;
        };

        // Increase base font size for better clarity
        const baseFontSize = fontSize; // Increased from 12
        const isBold = fontStyle && fontStyle.includes("bold");
        const isItalic = fontStyle && fontStyle.includes("italic");
        const isUnderline = fontStyle && fontStyle.includes("underline");

        let fontStyleString = "";
        if (isBold) fontStyleString += "bold ";
        if (isItalic) fontStyleString += "italic ";

        const family = getFontFamily(fontFamily, fontStyle);
        ctx.font = `${fontStyleString} ${baseFontSize}px ${family}`;

        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;

        // Increase padding for better visual appearance
        const paddingVertical = 12;
        const paddingHorizontal = 24;

        const bgWidth = textWidth + (paddingHorizontal * 2);
        const bgHeight = baseFontSize + (paddingVertical * 2);

        if (scaleX !== bgWidth || scaleY !== bgHeight) {
            dispatch(updateObjectInTargetImage({
                targetImageID: selectedTargetImageID ?? "",
                object: {
                    id: objectID,
                    type: ObjectTypes.TEXT,
                    properties: {
                        ...properties,
                        scaleX: bgWidth,
                        scaleY: bgHeight
                    }
                },
                skipHistory: true
            }))
        }

        // Rasterize at device-pixel resolution (capped at 2x) so the canvas texture matches
        // the plane's actual on-screen pixel size on HiDPI screens instead of always drawing
        // at CSS-pixel resolution.
        const scale = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = bgWidth * scale;
        canvas.height = bgHeight * scale;

        ctx.scale(scale, scale);

        // Apply the background color if it exists
        if (bgColor && bgColor !== "transparent") {
            ctx.fillStyle = bgColor;
            ctx.roundRect(0, 0, bgWidth, bgHeight, 8);
            ctx.fill();
        }

        const drawText = () => {
            // Ensure font is set correctly
            ctx.font = `${fontStyleString} ${baseFontSize}px ${family}`;
            ctx.fillStyle = fontColor;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            // Enable text sharpening
            ctx.imageSmoothingEnabled = false;

            ctx.fillText(text, bgWidth / 2, bgHeight / 2);

            if (isUnderline) {
                const underlineY = bgHeight / 2 + baseFontSize / 2;

                ctx.beginPath();
                ctx.moveTo(bgWidth / 2 - textWidth / 2, underlineY);
                ctx.lineTo(bgWidth / 2 + textWidth / 2, underlineY);
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = fontColor;
                ctx.stroke();
            }
        }

        if (typeof FontFaceObserver !== "undefined") {
            const font = new FontFaceObserver(family);
            font.load(null, 5000).then(
                () => {
                    console.log("Font loaded successfully:", family);
                    drawText();

                    // Create and return sprite after text is drawn
                    createAndReturnMesh();
                },
                () => {
                    console.warn("Font failed to load:", family);
                    drawText();

                    // Create and return sprite after text is drawn
                    createAndReturnMesh();
                }
            );
        } else {
            drawText();
            createAndReturnMesh();
        }

        function createAndReturnMesh() {
            // If a newer updateObjectText call started for this object while this rebuild was
            // waiting on font load, this generation is stale — bail out before touching the
            // scene at all. Mutating parentObject here would blindly rip out whatever mesh the
            // newer (and by now possibly already-resolved) generation added, since the removal
            // below only matches on the shared objectID, not on which generation added it.
            if (generation !== undefined && textUpdateGenerationRef.current.get(objectID) !== generation) {
                resolve(null);
                return;
            }

            // Create a texture from the canvas
            const texture = new THREE.CanvasTexture(canvas);
            texture.needsUpdate = true;

            // Set texture filtering for crisp text
            texture.minFilter = THREE.NearestFilter;
            texture.magFilter = THREE.NearestFilter;

            // Create a material with the texture
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                side: THREE.DoubleSide
            });

            const planeGeometry = new THREE.PlaneGeometry(bgWidth, bgHeight);
            const mesh = new THREE.Mesh(planeGeometry, material);

            mesh.position.set(positionX || 0, positionY || 0, 0);
            const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
            mesh.rotation.set(0, 0, rotationXRadians);

            // Previously this blindly reattached transformBoxRef.current (a single,
            // component-wide "last created box" ref, not scoped to this object) onto every
            // new mesh whenever any box happened to exist — including a stale box left over
            // from an unrelated, already-deleted object, since nothing reliably nulls this
            // ref on deletion (the cleanup effect only finds boxes still attached to live
            // scene children, and a deleted object's mesh is already gone by then). That
            // falsely marked fresh meshes as "already has a box", causing the real
            // box-creation logic (createObjectText/updateObjectText, gated correctly on
            // `objectID === selectedObjectID`) to skip creating one. Box reattachment for the
            // currently selected+tooled object is handled there instead.

            mesh.renderOrder = renderOrder;

            mesh.userData.isDraggable = true;
            mesh.userData.id = objectID;
            mesh.userData.objectType = ObjectTypes.TEXT;
            mesh.userData.originalWidth = bgWidth;
            mesh.userData.originalHeight = bgHeight;

            const existingChild = parentObject.children.find(child => child.userData.id === objectID);
            if (existingChild) {
                parentObject.remove(existingChild);
            }
            // Add the mesh only once
            parentObject.add(mesh);

            const result = {
                mesh: mesh,
                cleanup: () => {
                    if (parentObject) {
                        const childToRemove = parentObject.children.find(child => child.userData.id === objectID);
                        if (childToRemove) {
                            parentObject.remove(childToRemove);
                        }
                    }

                    if (mesh.geometry) mesh.geometry.dispose();
                    if (mesh.material) {
                        if (Array.isArray(mesh.material)) {
                            mesh.material.forEach(material => {
                                if (material.map) material.map.dispose();
                                material.dispose();
                            });
                        } else {
                            if (mesh.material.map) mesh.material.map.dispose();
                            mesh.material.dispose();
                        }
                    }

                    // The transform box lives as a sibling in sceneRef.current, not nested
                    // inside mesh — wiping userData below only drops the reference to it,
                    // leaving it as a permanently orphaned, undisposed ghost box unless
                    // removed explicitly here.
                    if (mesh.userData.transformBox) {
                        mesh.userData.transformBox.removeFromParent();
                        mesh.userData.transformBox.traverse((child: THREE.Object3D) => {
                            if (child instanceof Line2) {
                                if (child.geometry) child.geometry.dispose();
                                if (child.material) child.material.dispose();
                            }
                        });
                    }

                    mesh.userData = {};
                }
            }
            resolve(result);
            return result;
        }
    })
}
